import {
  Component,
  DestroyRef,
  effect,
  Inject,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AuthService, GenericError } from '@auth0/auth0-angular';
import {
  NS_CONFIG_TOKEN,
  NSNotificationModuleOptions,
  NSNotificationsComponent
} from '@bannerflow/notification';
import { NotificationDto } from '@bannerflow/types.notification-service';
import {
  UILogoComponent,
  UINewThemeService,
  UIPREnvPickerComponent
} from '@bannerflow/ui';
import { filter, interval, merge, of, switchMap, take } from 'rxjs';
import { TestTriggerComponent } from './test-trigger/test-trigger.component';

@Component({
  selector: 'app-root',
  imports: [
    UIPREnvPickerComponent,
    UILogoComponent,
    NSNotificationsComponent,
    TestTriggerComponent
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private uiNewThemeService = inject(UINewThemeService);

  private user = toSignal(this.authService.user$);

  title = 'NotificationService-demo';

  constructor(
    @Inject(NS_CONFIG_TOKEN) private libConfig: NSNotificationModuleOptions
  ) {
    this.uiNewThemeService.enableNewTheme();

    effect(() => {
      const user = this.user();

      if (user) {
        this.authService
          .getAccessTokenSilently({ cacheMode: 'on' })
          .pipe(take(1))
          .subscribe(accessToken =>
            this.libConfig.accessToken$.next(accessToken)
          );
        const intervalTime = 30 * 60 * 1000; // 30 mins
        merge(interval(intervalTime), of())
          .pipe(
            switchMap(() =>
              this.authService.getAccessTokenSilently({ cacheMode: 'on' })
            ),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe(accessToken => {
            this.libConfig.accessToken$.next(accessToken);
          });
      }
    });

    this.authService.error$
      .pipe(
        filter(
          e =>
            e instanceof GenericError &&
            (e.error === 'login_required' ||
              e.error === 'invalid_grant' ||
              e.error === 'missing_refresh_token')
        ),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.authService.loginWithRedirect();
      });
  }

  handleNewNotification(newNotification: NotificationDto): void {
    console.debug(newNotification);
  }
}
